import { X } from "lucide-react";

/**
 * Active-filter chip strip with per-chip clear + optional "Clear all".
 *
 * Props:
 *   chips     : Array<{ key: string, label: string, onClear?: () => void, tone?: "cyan"|"amber"|"slate" }>
 *   onClearAll: () => void (renders "Clear all" only if at least 2 chips AND provided)
 *   testId    : optional data-testid (default "filter-chips")
 *
 * Renders nothing when there are no chips.
 */
const TONE_MAP = {
  cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  slate: "bg-slate-50 text-slate-700 border-slate-200",
};

export function FilterChips({ chips = [], onClearAll, testId = "filter-chips" }) {
  if (!chips || chips.length === 0) return null;
  const showClearAll = onClearAll && chips.length >= 2;
  return (
    <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid={testId}>
      {chips.map((c) => {
        const tone = TONE_MAP[c.tone] || TONE_MAP.cyan;
        return (
          <span
            key={c.key}
            className={`inline-flex items-center gap-1 text-xs ${tone} border px-2 py-0.5 rounded-full`}
            data-testid={`${testId}-${c.key}`}
          >
            {c.label}
            {c.onClear ? (
              <button
                type="button"
                onClick={c.onClear}
                className="inline-flex items-center justify-center rounded-full hover:bg-black/5 ml-0.5"
                aria-label={`Remove filter ${c.label}`}
                data-testid={`${testId}-${c.key}-clear`}
              >
                <X className="w-3 h-3" />
              </button>
            ) : null}
          </span>
        );
      })}
      {showClearAll ? (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-slate-500 underline hover:text-slate-800"
          data-testid={`${testId}-clear-all`}
        >
          Clear all
        </button>
      ) : null}
    </div>
  );
}
